// Clone of LeadsPage. Same UI; "Generate Site" runs the templated generator
// in DemoStateProvider instead of calling the cloud function.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Phone, MapPin, Star, Sparkles, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { SiteCreationModal } from '@/components/site/SiteCreationModal';
import type { LayoutType } from '@/lib/templates';
import { HackerLoadingScreen } from '@/components/ui/HackerLoadingScreen';
import { useDemoState } from './DemoStateProvider';
import type { DemoLead } from './mockLeadsData';

export function DemoLeads() {
  const navigate = useNavigate();
  const { leads, generateSite } = useDemoState();
  const [search, setSearch] = useState('');
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<DemoLead | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showHackerScreen, setShowHackerScreen] = useState(false);
  const [generationLogs, setGenerationLogs] = useState<string[]>([]);

  const filteredLeads = leads.filter(
    (l) => !l.siteGenerated && l.businessName.toLowerCase().includes(search.toLowerCase()),
  );

  const openSiteCreationModal = (lead: DemoLead) => {
    setSelectedLead(lead);
    setShowModal(true);
  };

  const handleGenerateSite = async (templateId: string, selectedFeatures: string[], selectedLayouts: LayoutType[]) => {
    if (!selectedLead) return;
    setGeneratingId(selectedLead.id);
    setShowModal(false);
    setShowHackerScreen(true);

    const businessInfo = [
      `✓ Business: ${selectedLead.businessName}`,
      `✓ Location: ${selectedLead.location}`,
      `✓ Rating: ${selectedLead.rating} stars (${selectedLead.reviewCount} reviews)`,
      `✓ Category: ${selectedLead.niche}`,
      `✓ Template: ${templateId}`,
      selectedFeatures.length > 0 ? `✓ Features: ${selectedFeatures.join(', ')}` : null,
      selectedLayouts.length > 0 ? `✓ Layouts: ${selectedLayouts.join(', ')}` : null,
    ].filter(Boolean) as string[];

    setGenerationLogs(businessInfo);

    // Simulated progressive log feed for the hacker screen.
    const stages = [
      'Analyzing business profile...',
      'Generating copy with AI...',
      'Built Hero',
      'Built About',
      'Built Services',
      'Built Reviews',
      'Built Contact',
      'Applying template styles...',
      'Site ready!',
    ];

    for (const stage of stages) {
      await new Promise((r) => setTimeout(r, 280 + Math.random() * 220));
      setGenerationLogs((prev) => [...prev, `${stage.startsWith('Built') ? '✓' : '⟳'} ${stage}`]);
    }

    const { siteId } = await generateSite(selectedLead.id, templateId, selectedFeatures, selectedLayouts);

    setGenerationLogs((prev) => [...prev, '✓ Website generated successfully', '✓ Opening site editor...']);
    await new Promise((r) => setTimeout(r, 1200));

    setShowHackerScreen(false);
    toast.success(`Site created for ${selectedLead.businessName}!`);
    setSelectedLead(null);
    setGeneratingId(null);
    navigate(`/demo/sites/${siteId}/edit`);
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(15, 15, 15, 0.95)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'linear-gradient(to right, #B08080, #B08080)',
    color: '#000000',
  };

  const outlineButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#FFFBFE',
  };

  const badgeStyle = (variant: 'success' | 'warning'): React.CSSProperties => ({
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    background: variant === 'success' ? 'rgba(91,58,58,0.2)' : 'rgba(136,136,136,0.2)',
    color: variant === 'success' ? '#B08080' : '#888888',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Leads</h1>
        <p className="text-gray-400 mt-1">{leads.length} total leads</p>
      </div>

      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <input
          type="text"
          placeholder="Search leads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            color: '#FFFBFE',
            fontSize: '14px',
            outline: 'none',
          }}
        />
      </div>

      {filteredLeads.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="hover-lift animate-list-in" style={cardStyle}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flex: 1 }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(91,58,58,0.2), rgba(91,58,58,0.2))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#B08080',
                      flexShrink: 0,
                    }}>
                      <Users style={{ width: '24px', height: '24px' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE' }}>{lead.businessName}</h3>
                        {lead.rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#888888' }}>
                            <Star style={{ width: '16px', height: '16px', fill: 'currentColor' }} />
                            {lead.rating}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#888888', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin style={{ width: '16px', height: '16px' }} />
                          {lead.location}
                        </span>
                        <span>{lead.niche}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '8px', alignItems: 'center' }}>
                        {lead.contactMethod === 'email' || (lead.email && !lead.contactMethod) ? (
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: 'rgba(91,58,58,0.2)',
                            color: '#B08080',
                          }}>
                            <Mail style={{ width: '12px', height: '12px' }} />
                            Email
                          </span>
                        ) : lead.contactMethod === 'sms' || lead.smsCapable ? (
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: 'rgba(136,136,136,0.2)',
                            color: '#888888',
                          }}>
                            <MessageSquare style={{ width: '12px', height: '12px' }} />
                            SMS
                          </span>
                        ) : null}
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#B08080', textDecoration: 'none' }}>
                            <Mail style={{ width: '16px', height: '16px' }} />
                            {lead.email}
                          </a>
                        )}
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#888888', textDecoration: 'none' }}>
                            <Phone style={{ width: '16px', height: '16px' }} />
                            {lead.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {lead.hasWebsite && <span style={badgeStyle('warning')}>Has Website</span>}
                    {lead.siteGenerated ? (
                      <Link to="/demo/sites" style={{ ...outlineButtonStyle, textDecoration: 'none' }}>
                        <ExternalLink style={{ width: '16px', height: '16px' }} />
                        View Site
                      </Link>
                    ) : (
                      <button
                        onClick={() => openSiteCreationModal(lead)}
                        disabled={lead.hasWebsite || generatingId === lead.id}
                        style={{
                          ...primaryButtonStyle,
                          opacity: lead.hasWebsite || generatingId === lead.id ? 0.5 : 1,
                          cursor: lead.hasWebsite || generatingId === lead.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Sparkles style={{ width: '16px', height: '16px' }} />
                        {generatingId === lead.id ? 'Generating...' : 'Generate Site'}
                      </button>
                    )}
                  </div>
                </div>

                {lead.businessImages[0] && (
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, rgba(91,58,58,0.1), rgba(91,58,58,0.05))',
                  }}>
                    <img
                      src={lead.businessImages[0]}
                      alt={lead.businessName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(91,58,58,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Users style={{ width: '32px', height: '32px', color: '#B08080' }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE', marginBottom: '8px' }}>No Leads</h3>
          <p style={{ color: '#888888', marginBottom: '24px' }}>
            Go to Campaigns, create a campaign, and click "Scrape Leads" to find businesses
          </p>
          <Link to="/demo/campaigns" style={{ ...primaryButtonStyle, textDecoration: 'none' }}>
            Go to Campaigns
          </Link>
        </div>
      )}

      <SiteCreationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedLead(null);
        }}
        onSubmit={handleGenerateSite}
        businessName={selectedLead?.businessName || ''}
        loading={generatingId !== null}
      />

      <HackerLoadingScreen
        isOpen={showHackerScreen}
        title="GENERATING WEBSITE"
        subtitle={`Creating custom site for ${selectedLead?.businessName || 'business'}`}
        mode="generating"
        logs={generationLogs}
      />
    </div>
  );
}
